import { isNill, isString, replaceSpaces } from 'src/researcher/utils';
import { clone, generateRandomId } from 'core/utils';
import { CitationData, Contributor, ContributorTypes } from 'src/citation-generator/types';
import {
  EditFormAction,
  ErrorValidatorFunctions,
  FormValueKeys,
  WarningValidatorFunctions,
} from './types';

export const getDefaultContributorState = (): Contributor => ({
  key: generateRandomId(),
  contributorType: ContributorTypes.AUTHORS,
  firstName: '',
  lastName: '',
});

const errorValidators: ErrorValidatorFunctions = {
  empty: (val) => {
    if (!val) {
      return { error: ' is required' };
    }

    if (isString(val) && replaceSpaces((val || '') as string).trim?.() === '') {
      return { error: ' is required' };
    }

    return undefined;
  },
};

const warningValidators: WarningValidatorFunctions = {
  empty: (val) => {
    let out;
    let isEmpty = false;

    if (isNill(val)) {
      isEmpty = true;
    }

    if (isString(val) && replaceSpaces((val || '') as string).trim?.() === '') {
      isEmpty = true;
    }

    if (isEmpty) {
      out = { warning: true };
    }

    return out;
  },
  emptyDate: (val) => {
    if (!val || !val.year) {
      return { warning: true };
    }
    return undefined;
  },
  emptyContributors: (val) => {
    if (!val || !val.filter((v) => v.firstName || v.lastName).length) {
      // this helper function currently does not handle index-wise warnings // TODO
      return { warning: true };
    }
    return undefined;
  },
};

export const validators = {
  error: errorValidators,
  warn: warningValidators,
};

export const keyToAction = (key: FormValueKeys) =>
  `Set${key.charAt(0).toUpperCase()}${key.substring(1)}` as any;

export const actionToKey = (type: EditFormAction['type']) =>
  `${type.charAt(3).toLowerCase()}${type.substring(4)}`;

const normalizeContributor = (legacy: Contributor) => {
  if ('middleName' in legacy) {
    const out: Contributor = {
      ...legacy,
      firstName: [legacy.firstName, legacy.middleName].filter(Boolean).join(' ') || undefined,
    };

    out.initials = [legacy.firstName, legacy.middleName]
      .filter(Boolean)
      .map((str) => str?.toUpperCase())
      .join('');

    delete out.middleName;
    return out;
  }

  return legacy;
};

export const getBackwardsCompatibleData = (data: CitationData) => {
  const newData: CitationData = clone(data);

  if (data.publicationInfo.series) {
    newData.publicationInfo.collectionTitle = data.publicationInfo.series;
    delete newData.publicationInfo.series;
  }

  if (data.publicationInfo.journalTitle) {
    newData.publicationInfo.containerTitle = data.publicationInfo.journalTitle;
    delete newData.publicationInfo.journalTitle;
  }

  if (data.publicationInfo.websiteTitle) {
    newData.publicationInfo.containerTitle = data.publicationInfo.websiteTitle;
    delete newData.publicationInfo.websiteTitle;
  }

  if (newData.contributors.length) {
    newData.contributors = newData.contributors.map(normalizeContributor);
  } else {
    newData.contributors = [getDefaultContributorState()];
  }

  return newData;
};
